// src/App.js
import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Navigation from "./components/Navigation/Navigation";
import Scorecard from "./components/Scorecard/Scorecard";
import ScorecardHistory from "./components/Scorecard/ScorecardHistory";
import LowRound from "./components/LowRound/LowRound";
import Profile from "./components/Profile/Profile";
import AuthLanding from "./components/AuthLanding";
import Notifications from "./components/Notifications";
import SocialLinks from "./components/SocialLinks";
import VerifiedRoute from "./components/VerifiedRoute";
import Teams from "./components/Teams/Teams";
import AdminDashboard from "./components/Administration/AdminDashboard";
import ProfileView from "./components/Profile/ProfileView";
import { useAuth } from "./context/AuthContext";
import { getDoc, doc } from "firebase/firestore";
import { db } from "./firebase-config";
import { CircularProgress } from "@mui/material";

// Simple NotFound component for unknown routes.
const NotFound = () => (
  <div style={{ textAlign: "center", marginTop: "4rem" }}>
    <h2>404 - Page Not Found</h2>
    <p>The page you are looking for does not exist.</p>
  </div>
);

function App() {
  // Always call hooks at the top.
  const { user, initializing } = useAuth();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [notificationOverride, setNotificationOverride] = useState("");
  const [openRegister, setOpenRegister] = useState(false);
  const prevUidRef = useRef(null);
  const [waitingForVerification, setWaitingForVerification] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const uid = user?.uid ?? null;

  // Show login/logout snackbar message whenever UID changes.
  useEffect(() => {
    if (prevUidRef.current !== uid) {
      if (uid) {
        setSnackbarMessage("Login successful!");
      } else {
        setSnackbarMessage("Logged out!");
      }
      setSnackbarOpen(true);
      prevUidRef.current = uid;
    }
  }, [uid]);

  // Show override messages.
  useEffect(() => {
    if (notificationOverride) {
      setSnackbarMessage(notificationOverride);
      setSnackbarOpen(true);
      setNotificationOverride("");
    }
  }, [notificationOverride]);

  // Fetch the admin flag from Firestore if a user is logged in.
  useEffect(() => {
    const fetchIsAdmin = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setIsAdmin(userDoc.data().isAdmin === true);
          } else {
            setIsAdmin(false);
          }
        } catch (error) {
          console.error("Error fetching admin status:", error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    fetchIsAdmin();
  }, [user]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const openRegisterDialog = () => {
    setOpenRegister(true);
  };

  const handleRegistrationSuccess = () => {
    setWaitingForVerification(true);
  };

  // All hooks have been called unconditionally above.
  // Now conditionally render based on auth initialization.
  return (
    <>
      {initializing ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div className="App">
          {/* Pass admin flag to the navigation so it can conditionally render an admin link */}
          <Navigation onRegister={openRegisterDialog} isAdmin={isAdmin} />

          <Routes>
            {!user ? (
              <>
                <Route
                  path="/"
                  element={
                    <AuthLanding
                      openRegister={openRegister}
                      setOpenRegister={setOpenRegister}
                      onRegisterSuccess={handleRegistrationSuccess}
                      waitingForVerification={waitingForVerification}
                      setWaitingForVerification={setWaitingForVerification}
                    />
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              <>
                <Route
                  path="/scorecard"
                  element={
                    <VerifiedRoute>
                      <Scorecard />
                    </VerifiedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <VerifiedRoute>
                      <Profile />
                    </VerifiedRoute>
                  }
                />
                <Route
                  path="/scorecard-history"
                  element={
                    <VerifiedRoute>
                      <ScorecardHistory />
                    </VerifiedRoute>
                  }
                />
                <Route
                  path="/teams"
                  element={
                    <VerifiedRoute>
                      <Teams />
                    </VerifiedRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <VerifiedRoute>
                      <LowRound />
                    </VerifiedRoute>
                  }
                />
                <Route
                  path="/profile-view/:userId"
                  element={
                    <VerifiedRoute>
                      <ProfileView />
                    </VerifiedRoute>
                  }
                />
                {isAdmin && (
                  <Route
                    path="/admin"
                    element={
                      <VerifiedRoute>
                        <AdminDashboard />
                      </VerifiedRoute>
                    }
                  />
                )}
                {/* Instead of a forced redirect to "/", render a NotFound component for unmatched routes */}
                <Route path="*" element={<NotFound />} />
              </>
            )}
          </Routes>

          <Notifications
            open={snackbarOpen}
            handleClose={handleSnackbarClose}
            message={snackbarMessage}
            severity="success"
          />

          <SocialLinks />
        </div>
      )}
    </>
  );
}

export default App;